import React, { useState, useEffect } from 'react'
//components
import { Button } from '../../../../components/ui/buttons/Button'
import Label from '../../../../components/ui/labels/Label'
import Select from 'react-select'
import { SelectContainer } from '../../../../components/ui/layout/SelectContainer'
import { AddNewGroup } from './AddNewGroup'
import { PoiGroupDetails } from './PoiGroupDetails'
//hooks
import { useLoading } from '../../../../contexts/LoadingContext';
import { useError } from '../../../../contexts/ErrorContext'
import useConfirmDialog from '../../../../hooks/useConfirmDialog'
import usePoiGroupService from '../../../../hooks/pois/usePoiGroupsService'
import { useUser } from '../../../../contexts/UserContext'
//api
import { getPOIsByGroupAPI, deletePOIGroupAPI } from "../../../../services/poiGroupsAPI"
//context
import { useUnsavedChanges } from '@contexts/UnsavedChangesContext'
//utils
import { isUserAdmin } from '@utils/poi/isPoiEditDisabled'

/**
 * Add and update poi groups
 * 
 * @param {DropdownOption} selectedJob
 * 
 * @category Components - POI
 * 
 */
export const PoiGroups = ({
    selectedJob,
    storedPoiGroup,
    setStoredPoiGroup,
}) => {

    const [selectedGroup, setSelectedGroup] = useState(null) //group selected in select component
    const [groupDetails, setGroupDetails] = useState(null)
    const [isAddNewGroup, setIsAddNewGroup] = useState(false)

    const { showLoading, hideLoading } = useLoading();
    const {showError} = useError()
    const [ConfirmDialog, requestConfirm] = useConfirmDialog()
    const {permissionSortedPoiGroups, fetchPOIGroupsByJob, setPermissionSortedPoiGroups} = usePoiGroupService()
    const {setHasUnsavedChanges} = useUnsavedChanges()
    const { userGroups } = useUser()

    //get all groups and format names into options for select component
    const fetchPOIGroups = async () => {

        showLoading("Getting POI groups...")

        //get all of the user's groups using group service hook
        fetchPOIGroupsByJob(selectedJob.value, true)

        setSelectedGroup(null)
        
        hideLoading()
    }

    const fetchGroup = async() => {
        showLoading("Getting POI group...")

        const response = await getPOIsByGroupAPI(selectedGroup.value);

        if (!response.success) {
            hideLoading()

            showError(`Error getting POI groups: ${response.message}`)

            return
        }

        const poiGroupDetails = {
            ...response.data.poiGroupDetails, 
            IsEditor: selectedGroup.IsEditor, 
            IsUserInGroup: selectedGroup.IsUserInGroup, 
        }
        
        //set group details
        setGroupDetails(poiGroupDetails)

        hideLoading()
    }

    const handleDeletePOIGroup = async(poiGroupId) => {
        const isConfirmed = await requestConfirm('Are you sure you want to delete this POI group?', 'Delete POI Group', 'Cancel');
                
        if(!isConfirmed) return
        
        showLoading("Deleting POI group...")

        const response = await deletePOIGroupAPI(poiGroupId)
        
        if(!response.success){
            hideLoading()
        
            showError(`Error deleting POI group: ${response.message}`)
        
            return
                    
        }

        //remove group from nested dropdown
        setPermissionSortedPoiGroups(prevData => prevData.map(group => {
            if (group.options) { 
                return {
                    ...group,
                    options: group.options.filter(option => option.value !== groupDetails.POIGroupID)
                };
            }
            return group;
        }));

        //reset state
        setGroupDetails(null)
        setSelectedGroup(null)
        setStoredPoiGroup(null)
        setHasUnsavedChanges(false)
        hideLoading()

    }

    const handleChangeGroup = (group) => {
        setSelectedGroup(group)
        setStoredPoiGroup(group)
    }

    //fetch groups when component loads
    useEffect(() => {
        if(!selectedJob) return

        fetchPOIGroups()
    }, [selectedJob]); //eslint-disable-line

    //fetch group details when group is selected
    useEffect(() => {
        if(!selectedGroup) return

        fetchGroup()
    }, [selectedGroup]); //eslint-disable-line

      useEffect(() => {
        if(storedPoiGroup &&
            (!storedPoiGroup.label.includes("All POIs") && !storedPoiGroup.label.includes("Don't show"))
        ){
          setSelectedGroup(storedPoiGroup)
        }
      }, [storedPoiGroup])

    if(!selectedJob){
        return(
            <div>
                <p className='martrak-page-padding-left-right'>Please select a job</p>
            </div>
        )
    }
    
    return (
        <div className='martrak-page-padding-left-right'>

            {isAddNewGroup ? (
                <AddNewGroup 
                    setGroupDetails={setGroupDetails}
                    handleChangeGroup={handleChangeGroup} 
                    setPermissionSortedPoiGroups={setPermissionSortedPoiGroups}
                    isAddNewGroup={isAddNewGroup}
                    setIsAddNewGroup={setIsAddNewGroup}
                    selectedJob={selectedJob}
                />
            ) : (
                //if not "Add Group" form, show  Add New Group button and group dropdown 
                <>
                    {/* Add Group button */}
                    <div className='flex justify-end'>
                        <Button 
                            variant='success' 
                            onClick={() =>  {
                                setSelectedGroup(null)
                                setGroupDetails(null)
                                setIsAddNewGroup(true)
                                }} 
                            className='mb-4'>
                                <i className="fa-solid fa-circle-plus mr-2"></i>Add New Group
                        </Button>
                    </div>

                    {/* select a group */}
                    <Label htmlFor="groupsDropdown">Select a group</Label>
                    <SelectContainer>
                        <Select
                            value={selectedGroup}
                            options={permissionSortedPoiGroups}
                            name="groupsDropdown"
                            onChange={(x) => handleChangeGroup(x)}
                            menuPosition='fixed'
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                    </SelectContainer>

                    {/*  if group is selected and data fetched, show group details*/}
                    {groupDetails
                        ? 
                        <>
                            <PoiGroupDetails 
                                groupDetails={groupDetails} 
                                setSelectedGroup={setSelectedGroup} 
                                setPermissionSortedPoiGroups={setPermissionSortedPoiGroups}
                                isAddNewGroup={isAddNewGroup}
                                setIsAddNewGroup={setIsAddNewGroup}
                                selectedJob={selectedJob}
                                setStoredPoiGroup={setStoredPoiGroup}
                            /> 
                            
                        </>
                        : null 
                    }

                    {/* if user has permissions to delete group, show delete button */}
                    {selectedGroup && (selectedGroup.IsEditor || isUserAdmin(userGroups)) ? (
                        <Button
                            variant='danger' 
                            onClick={() => handleDeletePOIGroup(selectedGroup.value)} 
                            className='mb-4'
                        >
                            Delete POI Group
                        </Button>
                    ) : null}

                    {/* dialog box to confirm  POI group deletion */}
                    <ConfirmDialog />
                </>
            )}

        </div>
    )
}